import { Share2, Copy, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const ShareButton = ({ launch, className = "" }) => {
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/launches/${launch.id}`;
  const shareText = `Check out this upcoming launch: ${launch.name} - ${launch.rocket} from ${launch.pad_name}`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: launch.name,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Share failed:", err);
        }
      }
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const shareToTwitter = () => {
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(tweetUrl, "_blank", "noopener,noreferrer");
  };

  const shareToFacebook = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(fbUrl, "_blank", "noopener,noreferrer");
  };

  // If native share is available, use it directly
  if (navigator.share) {
    return (
      <button
        onClick={handleShare}
        className={`p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 ${className}`}
        title="Share"
        data-testid={`share-btn-${launch.id}`}
        style={{ transition: "background-color 0.2s ease" }}
      >
        <Share2 className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
      </button>
    );
  }

  // Fallback to dropdown menu
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 ${className}`}
          title="Share"
          data-testid={`share-btn-${launch.id}`}
          style={{ transition: "background-color 0.2s ease" }}
        >
          <Share2 className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-deep-void border-white/10">
        <DropdownMenuItem onClick={copyToClipboard} className="cursor-pointer">
          {copied ? (
            <Check className="w-4 h-4 mr-2 text-emerald-400" strokeWidth={1.5} />
          ) : (
            <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />
          )}
          Copy Link
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareToTwitter} className="cursor-pointer">
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          Share on X
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareToFacebook} className="cursor-pointer">
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          Share on Facebook
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
